/**
 * Efeitos especiais dos dados 3D (docs/spec/dice-3d.md, "Efeitos especiais"): quem rola escolhe
 * regras como "o d20 que cai no 20 pega fogo", e elas vão junto com a rolagem para todos verem.
 *
 * Aqui ficam as regras, o catálogo de ids e a escolha de qual dado ganha qual efeito. O desenho de
 * cada efeito é do cliente (apps/client/src/dice/effects/).
 */
import { z } from 'zod';

/** Catálogo. Efeito novo: o id aqui, a definição no cliente e o nome nas traduções. */
export const DICE_EFFECT_IDS = ['fire', 'ice', 'acid', 'stone', 'tavern', 'casino', 'nature', 'divine', 'lightning'] as const;
export type DiceEffectId = (typeof DICE_EFFECT_IDS)[number];

/** Dados que aceitam efeito: os que aparecem no palco 3D (o d100 vale pelos seus dois d10). */
export const EFFECT_DICE = [4, 6, 8, 10, 12, 20, 100] as const;
export type EffectDieSides = (typeof EFFECT_DICE)[number];

/** Regras de uma pessoa (a ordem conta: no mesmo dado, vale a primeira que bater). */
export const MAX_DICE_EFFECT_RULES = 8;

export const DiceEffectRuleSchema = z.object({
  sides: z.union([z.literal(4), z.literal(6), z.literal(8), z.literal(10), z.literal(12), z.literal(20), z.literal(100)]),
  /** maior face, menor face ou um valor exato */
  face: z.union([z.literal('max'), z.literal('min'), z.number().int().min(1).max(100)]),
  /** id do catálogo; um id que esta versão não conhece passa aqui e é ignorado (versão mais nova) */
  effect: z.string().regex(/^[a-z0-9-]{1,32}$/),
});
export type DiceEffectRule = z.infer<typeof DiceEffectRuleSchema>;

export const DiceEffectRulesSchema = z.array(DiceEffectRuleSchema).max(MAX_DICE_EFFECT_RULES);

/** O padrão do RPG Desk, para quem não mexeu nos efeitos: o d20 que cai no 20 pega fogo. */
export const DEFAULT_DICE_EFFECTS: readonly DiceEffectRule[] = [{ sides: 20, face: 'max', effect: 'fire' }];

export function isKnownDiceEffect(id: string): id is DiceEffectId {
  return (DICE_EFFECT_IDS as readonly string[]).includes(id);
}

/**
 * Só as regras que valem: efeito conhecido e valor que o dado tem. É o ponto único de conferência
 * do servidor (onde efeitos exclusivos entrariam).
 */
export function sanitizeDiceEffects(rules: readonly DiceEffectRule[]): DiceEffectRule[] {
  return rules.filter((rule) => isKnownDiceEffect(rule.effect) && (typeof rule.face !== 'number' || rule.face <= rule.sides)).slice(0, MAX_DICE_EFFECT_RULES);
}

function faceMatches(face: DiceEffectRule['face'], die: { sides: number; value: number }): boolean {
  if (face === 'max') return die.value === die.sides;
  if (face === 'min') return die.value === 1;
  return die.value === face;
}

/** Efeito de um dado da rolagem (`die`: posição dele em `RollResult.dice`). */
export type DiceEffectMatch = { die: number; effect: DiceEffectId };

/**
 * Que dado ganha que efeito. Sem regras (`undefined`: rolagem antiga, plugin, cliente antigo),
 * vale o padrão do RPG Desk; uma lista vazia é "sem efeitos". Cada dado ganha no máximo um efeito.
 */
export function matchDiceEffects(rules: readonly DiceEffectRule[] | undefined, dice: readonly { sides: number; value: number }[]): DiceEffectMatch[] {
  const active = rules ?? DEFAULT_DICE_EFFECTS;
  const out: DiceEffectMatch[] = [];
  dice.forEach((die, index) => {
    const rule = active.find((candidate) => candidate.sides === die.sides && isKnownDiceEffect(candidate.effect) && faceMatches(candidate.face, die));
    if (rule) out.push({ die: index, effect: rule.effect as DiceEffectId });
  });
  return out;
}
